import {
  Component, OnInit, OnDestroy, NgZone, forwardRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor, NG_VALUE_ACCESSOR,
  ReactiveFormsModule, FormControl,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent }
  from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Loader } from '@googlemaps/js-api-loader';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface OnboardAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  placeId?: string;
  formattedAddress?: string;
}

@Component({
  selector: 'app-address-autocomplete',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule,
    MatFormFieldModule, MatInputModule, MatIconModule,
    MatAutocompleteModule, MatProgressSpinnerModule,
  ],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AddressAutocompleteComponent),
    multi: true,
  }],
  template: `
    <mat-form-field appearance="outline" class="full-width">
      <mat-label>{{ label }}</mat-label>
      <mat-icon matPrefix>location_on</mat-icon>
      <input matInput [formControl]="inputCtrl"
             [matAutocomplete]="auto"
             placeholder="Start typing your address…"
             autocomplete="off">
      <mat-spinner matSuffix *ngIf="loading" diameter="18"
                   style="margin-right:8px"></mat-spinner>
      <mat-autocomplete #auto="matAutocomplete"
                        (optionSelected)="selectSuggestion($event)">
        <mat-option *ngFor="let s of suggestions"
                    [value]="s.placePrediction.text.text">
          <span>{{ s.placePrediction.mainText.text }}</span>
          <small style="color:#888;margin-left:4px">
            {{ s.placePrediction.secondaryText?.text }}
          </small>
        </mat-option>
      </mat-autocomplete>
      <mat-hint *ngIf="selectedAddress && (selectedAddress.city || selectedAddress.state || selectedAddress.postal_code)">
        {{ selectedAddress.city }}{{ (selectedAddress.city && selectedAddress.state) ? ', ' : '' }}{{ selectedAddress.state }} {{ selectedAddress.postal_code }}
      </mat-hint>
    </mat-form-field>
  `,
})
export class AddressAutocompleteComponent
  implements OnInit, OnDestroy, ControlValueAccessor {

  label = 'Address';
  inputCtrl        = new FormControl('');
  selectedAddress: OnboardAddress | null = null;
  suggestions: any[] = [];
  loading          = false;

  private placesLib: any     = null;
  private sessionToken: any  = null;
  private sub: Subscription | null = null;
  private lastSelectedText: string | null = null;

  private onChange  = (_: OnboardAddress | null) => {};
  private onTouched = () => {};

  constructor(private ngZone: NgZone) {}

  async ngOnInit(): Promise<void> {
    const loader = new Loader({ apiKey: environment.googleMapsApiKey });
    this.placesLib = await loader.importLibrary('places');
    this.sessionToken = new this.placesLib.AutocompleteSessionToken();

    this.sub = this.inputCtrl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe(async (raw) => {
      const input = raw as string | null;

      if (!input || input.length < 3) {
        this.ngZone.run(() => { this.suggestions = []; });
        return;
      }

      if (this.lastSelectedText && input === this.lastSelectedText) {
        this.lastSelectedText = null;
        this.ngZone.run(() => { this.suggestions = []; });
        return;
      }

      if (this.selectedAddress &&
          this.selectedAddress.formattedAddress !== input) {
        this.selectedAddress = null;
        this.onChange(null);
      }

      this.ngZone.run(() => { this.loading = true; });
      try {
        const { AutocompleteSuggestion } = this.placesLib;
        const res = await AutocompleteSuggestion
          .fetchAutocompleteSuggestions({
            input,
            sessionToken: this.sessionToken,
            includedRegionCodes: ['us'],
          });
        this.ngZone.run(() => {
          this.suggestions = res?.suggestions ?? [];
          this.loading = false;
        });
      } catch {
        this.ngZone.run(() => {
          this.suggestions = [];
          this.loading = false;
        });
      }
    });
  }

  async selectSuggestion(event: MatAutocompleteSelectedEvent): Promise<void> {
    const selectedText: string = event.option.value;
    const suggestion = this.suggestions.find(
      s => s.placePrediction.text.text === selectedText,
    );
    if (!suggestion) return;

    this.loading = true;
    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'id'],
      });

      const get = (type: string): string =>
        place.addressComponents
          ?.find((c: any) => c.types.includes(type))?.shortText ?? '';

      const addr: OnboardAddress = {
        line1: `${get('street_number')} ${get('route')}`.trim(),
        city: place.addressComponents
                 ?.find((c: any) => c.types.includes('locality'))
                 ?.longText ?? '',
        state: get('administrative_area_level_1'),
        postal_code: get('postal_code'),
        country: get('country'),
        placeId: place.id,
        formattedAddress: place.formattedAddress,
      };

      this.sessionToken = new this.placesLib.AutocompleteSessionToken();

      this.ngZone.run(() => {
        this.selectedAddress  = addr;
        this.suggestions      = [];
        this.loading          = false;
        this.lastSelectedText = addr.formattedAddress ?? addr.line1;
        this.inputCtrl.setValue(this.lastSelectedText, { emitEvent: false });
        this.onChange(addr);
        this.onTouched();
      });
    } catch {
      this.ngZone.run(() => { this.loading = false; });
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  writeValue(addr: OnboardAddress | null): void {
    this.selectedAddress = addr;
    let display = '';
    if (addr && (addr.line1 || addr.city || addr.state || addr.postal_code)) {
      if (addr.formattedAddress) {
        display = addr.formattedAddress;
      } else {
        const parts = [
          addr.line1,
          [addr.city, addr.state].filter(Boolean).join(', '),
          addr.postal_code,
        ].filter(Boolean);
        display = parts.join(', ');
      }
    }
    this.inputCtrl.setValue(display, { emitEvent: false });
    this.lastSelectedText = display || null;
  }

  registerOnChange(fn: (_: OnboardAddress | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.inputCtrl.disable() : this.inputCtrl.enable();
  }
}
